type Equivalency = {
  factor: number;
  unit: string;
};

export type ReceivableProduct = {
  value: string;
  name: string | undefined;
  code: string | undefined;
  baseUnit: string;
  equivalencies: Equivalency[];
  quantity: string;
  receivedQuantity: number;
};

export type MergedProductsData = {
  value: string;
  name: string | undefined;
  code: string | undefined;
  unit: string;
  quantity: number;
  pendingQuantity: number;
};

export type Entry = [productId: string, productData: ReceivableProduct];

export const mergeProducts = (data: Entry[]): MergedProductsData[] => {
  return Object.values(
    data.reduce<{ [key: string]: MergedProductsData }>(
      (acc, [productId, productData]) => {
        const existingProductData = acc[productId];

        const {
          equivalencies,
          quantity: quantityWithUnit,
          baseUnit,
          receivedQuantity,
          ...rest
        } = productData;

        const lineQuantity = toBaseUnit({
          quantityWithUnit,
          baseUnit,
          equivalencies
        });

        const totalQuantity =
          (existingProductData?.quantity ?? 0.0) + lineQuantity;

        return {
          ...acc,
          [productId]: {
            ...existingProductData,
            ...rest,
            unit: baseUnit,
            quantity: totalQuantity,
            pendingQuantity: totalQuantity - receivedQuantity
          }
        };
      },
      {}
    )
  );
};

export const toBaseUnit = ({
  quantityWithUnit,
  baseUnit,
  equivalencies
}: {
  quantityWithUnit: string;
  baseUnit: string;
  equivalencies: Equivalency[];
}): number => {
  const found = quantityWithUnit.match(
    /(?<quantity>\d+(\.\d+)?)\s+(?<unit>.*)/
  );
  const { quantity, unit } =
    found?.groups && found.groups.quantity
      ? found.groups
      : { quantity: 1, unit: baseUnit };

  const equivalency = equivalencies.find(({ unit: eUnit }) => unit === eUnit);

  return equivalency
    ? parseFloat(`${quantity ?? "0.0"}`) * equivalency.factor
    : parseFloat(`${quantity ?? "0.0"}`);
};
